import type { DesignerDocument, EditCommand, TokenOverride, TokenPath } from '../types.js';
import { withOverrides } from '../types.js';

/**
 * Command that sets (or updates) a single token override.
 *
 * - apply(): adds/updates the override in the document
 * - revert(): restores the previous value, or removes the override if none existed
 */
export class SetTokenCommand implements EditCommand {
  readonly id: string;
  readonly description: string;

  private readonly path: TokenPath;
  private readonly newValue: string;
  private readonly originalValue: string;
  private previousOverride: TokenOverride | undefined;

  constructor(path: TokenPath, newValue: string, originalValue: string) {
    this.path = path;
    this.newValue = newValue;
    this.originalValue = originalValue;
    this.id = `set-token:${path.cssVar}:${Date.now()}`;
    this.description = `Set ${path.cssVar} to ${newValue}`;
  }

  apply(doc: DesignerDocument): DesignerDocument {
    // Capture previous state for revert
    this.previousOverride = doc.overrides.get(this.path.cssVar);

    const next = new Map(doc.overrides);
    next.set(this.path.cssVar, {
      path: this.path,
      value: this.newValue,
      originalValue: this.originalValue,
    });
    return withOverrides(doc, next);
  }

  revert(doc: DesignerDocument): DesignerDocument {
    const next = new Map(doc.overrides);
    if (this.previousOverride) {
      next.set(this.path.cssVar, this.previousOverride);
    } else {
      next.delete(this.path.cssVar);
    }
    return withOverrides(doc, next);
  }
}
